import React from 'react';

import { LinkCard } from '../../../../../components/Cards/LinkCard/LinkCard';
import { Heading } from '../../../../../components/Heading/Heading';
import { Text } from '../../../../../components/Text/Text';
import { useAnalytics } from '../../../../../contexts/AnalyticsContext/AnalyticsContext';
import { getNodesFromConnection } from '../../../../../helpers/graphql';
import { getFishingWaterUrl } from '../../../../../helpers/urls';
import { EventMykissCardClicked } from '../../../../../interfaces/events';
import { GetFishingWaterNearbyWaters_fishingWater_suggestedWaters } from '../../../../../interfaces/graphql';
import { convertCount } from '../../../../../helpers/conversion';

export interface INearbyWaterCardProps {
  className?: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  /* The suggested water to display */
  suggestedWater: GetFishingWaterNearbyWaters_fishingWater_suggestedWaters;
}

export const NearbyWaterCard = ({
  className = '',
  clickEventSource,
  clickEventPosition,
  clickEventSourceId,
  suggestedWater,
}: INearbyWaterCardProps) => {
  const { currentPage } = useAnalytics();
  const {
    _id: waterId,
    displayName,
    catchesCount,
    country,
    region,
    topSpeciesSummary,
    newCatchCount,
  } = suggestedWater;
  const regionName = region?.name;
  const countryName = country?.countryName;
  const locationString = `${regionName ? `${regionName}, ` : ''}${countryName}`;

  const numberOfNewCatches = newCatchCount.totalCount;
  const topSpecies = getNodesFromConnection(topSpeciesSummary);

  return (
    <LinkCard
      to={getFishingWaterUrl(waterId, displayName)}
      eventProperties={{
        source: clickEventSource,
        target: 'fishing_water',
        target_id: waterId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      className={`bg-lake-fog rounded-lg ${className}`}
    >
      <div className="bg-deep-sea flex flex-col items-center justify-center gap-px rounded-t-lg px-2 py-3 md:px-4 md:py-6">
        <Heading as="div" variant="2xs" className="text-sea-foam m-0 truncate">
          {displayName}
        </Heading>
        {regionName && countryName && (
          <Text variant="xs" className="text-sea-foam m-0">
            {locationString}
          </Text>
        )}
      </div>
      <div className="flex w-full flex-col gap-2 p-4 md:gap-1.5 md:p-6">
        <div className="flex flex-row items-center justify-start gap-2">
          <Text variant="sm" className="m-0 font-semibold">
            {`${convertCount(catchesCount)} logged catch${catchesCount === 1 ? '' : 'es'}`}
          </Text>
          {numberOfNewCatches > 0 && (
            <Text
              variant="xs"
              className="bg-clownfish-shade m-0 rounded-md px-1 py-0.5 font-semibold text-white"
            >
              {`${numberOfNewCatches} new`}
            </Text>
          )}
        </div>

        {topSpecies.length > 0 && (
          <Text variant="sm" className="m-0">
            <span className="inline font-semibold">Top species: </span>
            {topSpecies.map((species, index) => {
              return `${species.displayName}${index < topSpecies.length - 1 ? ', ' : ''}`;
            })}
          </Text>
        )}
      </div>
    </LinkCard>
  );
};
